import { getLogger, type LayoutOverrideProps, useScrollToAnchor, useScrollToTop } from '@omnia/ui/src/lib'
import { RootLevelErrorBoundary } from '@omnia/ui/src/lib/cms/core-components/rootLevelErrorBoundary/RootLevelErrorBoundary'
import useNotificationObserver from '@omnia/ui/src/lib/hooks/useNotificationObserver'
import { getOrder, orderDomainStore } from '@vista-digital/occ-domain'
import { NotificationListComponent } from '@vista-digital/occ-ui'
import { ErrorBoundary } from '@vista-digital/react-tools'
import type { ErrorInfo, FC } from 'react'
import { memo, Suspense, useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom-v7'
import { NotificationBannersList } from '@/common/components/notificationBannersList/NotificationBannersList'
import Popups from '@/common/components/popup/Popups'
import { useGtmMembershipTier } from '@/common/services/gtm/hooks/useGtmPageViewEffect'
import { AbandonOrderModal } from '@/features/ticketing/components/abandonOrderModal/AbandonOrderModal'
import { ticketingWorkflow } from '@/pages/ticketing/ticketing-workflow'
import { Footer } from './footer/Footer'
import { Header } from './header/Header'

export const Layout: FC<LayoutOverrideProps> = memo(({ rootLevelErrorBoundaryOverride }) => {
    const logRootLevelError = (error: Error, info: ErrorInfo) => {
        const loggerRoot = getLogger(['RootLevelErrorBoundary', error.name]).value
        loggerRoot.error(error.message, { stack: error.stack, componentStack: info.componentStack })
    }

    useScrollToAnchor()
    useScrollToTop()
    useNotificationObserver()
    useGtmMembershipTier()

    const ErrorBoundaryElement = rootLevelErrorBoundaryOverride ?? RootLevelErrorBoundary

    const navigate = useNavigate()
    const location = useLocation()

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState !== 'visible') return
            orderDomainStore.hydrate()
            ticketingWorkflow.hydrateState()
            // 'Another order in progress' component in omnia ui is checked only when the location changes
            void navigate(location, {
                replace: true,
                state: {
                    orderId: getOrder()?.id,
                },
                relative: 'route',
            })
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [location])

    return (
        <ErrorBoundary
            fallback={
                <Suspense>
                    <ErrorBoundaryElement />
                </Suspense>
            }
            onError={logRootLevelError}
        >
            <NotificationBannersList />
            <Header />
            <Popups />
            <main className="page-wrapper">
                <Outlet />
            </main>
            <NotificationListComponent />
            <AbandonOrderModal />
            <Footer />
        </ErrorBoundary>
    )
})
